import { useRef, useState } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { AgGridReact } from 'ag-grid-react';
import { AutoCompleteSelectEvent } from 'primereact/autocomplete';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';
import { InputText } from 'primereact/inputtext';
import { classNames } from 'primereact/utils';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import { useCreateNewSireMutation } from '@/app/services/donorSchedules';
import { addToast } from '@/app/slices/toast';
import { CalendarWrapper as Calendar } from '@/components/common/calendar-wrapper';
import { ToastLife } from '@/components/hoc/toast-container/enums';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useRevisionIncrement } from '@/hooks/useRevisionIncrement';
import { useCreateSireMutation } from '@/services/sire';
import { verifiedOptions } from '@/types/common';
import { appendRevisionComment } from '@/utils/formatting';

import { BreedsAutoComplete } from '../breeds-auto-complete/BreedsAutoComplete';
import { sireFormSchema, SireFormValues } from '../new-sire-dialog/schema';
import { SpeciesDropdown } from '../species-dropdown/SpeciesDropdown';

interface INewSireDialogProps {
  visible: boolean;
  onHide: () => void;
  initialValues?: Partial<SireFormValues>;
  gridRef?: React.RefObject<AgGridReact<any>>;
  revisionComment: string;
  setRevisionComment: (revisionComment: string) => void;
  revisionNumber: {
    previousRevision: string;
    currentRevision: string;
  };
  setRevisionNumber: (revisionNumber: {
    previousRevision: string;
    currentRevision: string;
  }) => void;
}

export const NewSireDialog = ({
  visible,
  onHide,
  initialValues,
  gridRef,
  revisionComment,
  setRevisionComment,
  revisionNumber,
  setRevisionNumber,
}: INewSireDialogProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [createSire] = useCreateNewSireMutation();
  const { mutateAsync: createSireRest } = useCreateSireMutation();
  const isSireVerifiedEnabled = useFeatureFlag('Sire_Verified_Field');
  const [params] = useSearchParams();
  const isRevisionMode = params.get('revision') === 'true';
  const { incrementRevisionWithTopRevisions } = useRevisionIncrement();
  const [selectedSubFamilies, setSelectedSubFamilies] = useState<number[]>([]);
  const dialogRef = useRef<Dialog>(null);
  const [dialogContainer, setDialogContainer] = useState<HTMLElement>();

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm<SireFormValues>({
    resolver: zodResolver(sireFormSchema),
    defaultValues: {
      name: undefined,
      breedId: undefined,
      sire: undefined,
      regPrefix: undefined,
      registrationNumber: undefined,
      sireTpi: undefined,
      sireCm: undefined,
      sireJpi: undefined,
      sireNm: undefined,
      cane: undefined,
      sexedCane: undefined,
      birth: undefined,
      heparin: 30,
      breedReg: undefined,
      verified: 'No',
      ...initialValues,
    },
  });

  const getFormErrorMessage = (name: keyof SireFormValues) => {
    return (
      errors[name] && (
        <small className="mt-1 text-sm text-red-500">
          {errors[name]?.message}
        </small>
      )
    );
  };

  const showSaveWarning = (message: string | null | undefined) =>
    dispatch(
      addToast({
        severity: 'warn',
        summary: t('common.error'),
        detail: message,
        life: ToastLife.MEDIUM,
      }),
    );

  const onSave = async ({ verified, ...input }: SireFormValues) => {
    try {
      if (isSireVerifiedEnabled) {
        const res = await createSireRest({
          ...input,
          verified: verified === 'Yes',
        });
        if (!res.success) {
          showSaveWarning(res.message);
          return;
        }
      } else {
        const res = await createSire({ input }).unwrap();
        if (res.createNewSire.success === false) {
          showSaveWarning(res.createNewSire.message);
        }
      }
      if (params.get('revision') === 'true') {
        const newNoteText = `Input Sire, ${input.name}`;
        setRevisionComment(appendRevisionComment(revisionComment, newNoteText));
        setRevisionNumber(
          incrementRevisionWithTopRevisions(
            revisionNumber.previousRevision,
            revisionNumber.currentRevision,
          ),
        );
      }
      gridRef?.current?.api.refreshServerSide();
      gridRef?.current?.api.redrawRows();
      reset();
      onHide();
    } catch (error) {
      console.error('Failed to create Sire:', error);
    }
  };

  const footerContent = (
    <>
      <Button
        label={t('common.cancel')}
        icon="pi pi-times"
        severity="secondary"
        onClick={() => {
          reset();
          onHide();
        }}
      />
      <Button
        label={t('common.save')}
        icon="pi pi-check"
        onClick={handleSubmit(onSave)}
        className={isRevisionMode ? 'border-yellow-500 bg-yellow-500' : ''}
        tooltip={isRevisionMode ? t('revision.revisionModeFieldTooltip') : ''}
      />
    </>
  );

  return (
    <Dialog
      ref={dialogRef}
      header={t('sire.newSire')}
      visible={visible}
      onHide={onHide}
      footer={footerContent}
      modal
      className="w-[1200px] max-w-[90vw]"
      onShow={() =>
        setDialogContainer(dialogRef.current?.getMask() ?? undefined)
      }
    >
      <form
        onSubmit={handleSubmit(onSave)}
        className="grid grid-cols-3 gap-4 p-4"
      >
        <div className="label-input-group">
          <label htmlFor="name">{t('fields.shortName')}*</label>
          <Controller
            name="name"
            control={control}
            render={({ field, fieldState }) => (
              <InputText
                id={field.name}
                value={field.value}
                className={classNames('w-full', {
                  'p-invalid': fieldState.invalid,
                })}
                onChange={(e) => field.onChange(e.target.value)}
                tabIndex={1}
              />
            )}
          />
          {getFormErrorMessage('name')}
        </div>

        <SpeciesDropdown
          label={t('fields.species', 'Species')}
          tabIndex={2}
          onChange={(subFamilies) => {
            setSelectedSubFamilies(subFamilies);
            setValue('breedId', '');
          }}
        />

        <div className="label-input-group">
          <Controller
            name="breedId"
            control={control}
            render={({ field, fieldState }) => (
              <BreedsAutoComplete
                key={selectedSubFamilies.join(',')}
                label={`${t('fields.breed')}*`}
                className={classNames('w-full', {
                  'p-invalid': fieldState.invalid,
                })}
                subFamilies={selectedSubFamilies}
                tabIndex={3}
                onSelect={(e: AutoCompleteSelectEvent) => {
                  field.onChange(e.value?.id);
                }}
              />
            )}
          />
          {getFormErrorMessage('breedId')}
        </div>

        <div className="label-input-group">
          <label htmlFor="sire">{t('sire.pedigreeName')}*</label>
          <Controller
            name="sire"
            control={control}
            render={({ field, fieldState }) => (
              <InputText
                id={field.name}
                value={field.value}
                className={classNames('w-full', {
                  'p-invalid': fieldState.invalid,
                })}
                onChange={(e) => field.onChange(e.target.value)}
                tabIndex={4}
              />
            )}
          />
          {getFormErrorMessage('sire')}
        </div>

        <div className="label-input-group">
          <label htmlFor="regPrefix">{t('fields.countryCode')}</label>
          <Controller
            name="regPrefix"
            control={control}
            render={({ field, fieldState }) => (
              <InputText
                id={field.name}
                value={field.value}
                className={classNames('w-full', {
                  'p-invalid': fieldState.invalid,
                })}
                onChange={(e) => field.onChange(e.target.value)}
                tabIndex={5}
              />
            )}
          />
          {getFormErrorMessage('regPrefix')}
        </div>

        <div className="label-input-group">
          <label htmlFor="registrationNumber">
            {t('fields.registrationNumber')}
          </label>
          <Controller
            name="registrationNumber"
            control={control}
            render={({ field, fieldState }) => (
              <InputText
                id={field.name}
                value={field.value}
                className={classNames('w-full', {
                  'p-invalid': fieldState.invalid,
                })}
                onChange={(e) => field.onChange(e.target.value)}
                tabIndex={6}
              />
            )}
          />
          {getFormErrorMessage('registrationNumber')}
        </div>

        <div className="label-input-group">
          <label htmlFor="sireTpi">{t('sire.tpi')}</label>
          <Controller
            name="sireTpi"
            control={control}
            render={({ field }) => (
              <InputNumber
                id={field.name}
                value={field.value}
                className="w-full"
                onChange={(e) => field.onChange(e.value)}
                tabIndex={7}
              />
            )}
          />
        </div>

        <div className="label-input-group">
          <label htmlFor="sireCm">{t('sire.cmDollar')}</label>
          <Controller
            name="sireCm"
            control={control}
            render={({ field }) => (
              <InputNumber
                id={field.name}
                value={field.value}
                className="w-full"
                onChange={(e) => field.onChange(e.value)}
                tabIndex={8}
              />
            )}
          />
        </div>

        <div className="label-input-group">
          <label htmlFor="sireJpi">{t('sire.jpi')}</label>
          <Controller
            name="sireJpi"
            control={control}
            render={({ field }) => (
              <InputNumber
                id={field.name}
                value={field.value}
                className="w-full"
                onChange={(e) => field.onChange(e.value)}
                tabIndex={9}
              />
            )}
          />
        </div>

        <div className="label-input-group">
          <label htmlFor="sireNm">{t('sire.nm')}</label>
          <Controller
            name="sireNm"
            control={control}
            render={({ field }) => (
              <InputNumber
                id={field.name}
                value={field.value}
                className="w-full"
                onChange={(e) => field.onChange(e.value)}
                tabIndex={10}
              />
            )}
          />
        </div>

        <div className="label-input-group">
          <label htmlFor="cane">{t('sire.caneNaab')}</label>
          <Controller
            name="cane"
            control={control}
            render={({ field }) => (
              <InputText
                id={field.name}
                value={field.value}
                className="w-full"
                onChange={(e) => field.onChange(e.target.value)}
                tabIndex={11}
              />
            )}
          />
        </div>

        <div className="label-input-group">
          <label htmlFor="sexedCane">{t('sire.sexedCaneCode')}</label>
          <Controller
            name="sexedCane"
            control={control}
            render={({ field }) => (
              <InputText
                id={field.name}
                value={field.value}
                className="w-full"
                onChange={(e) => field.onChange(e.target.value)}
                tabIndex={12}
              />
            )}
          />
        </div>

        <div className="label-input-group">
          <label htmlFor="birth">{t('fields.birthDate')}</label>
          <Controller
            name="birth"
            control={control}
            render={({ field }) => (
              <Calendar
                id={field.name}
                value={field.value}
                className="w-full"
                onChange={(e) => field.onChange(e.value)}
                showIcon
                dateFormat="mm/dd/yy"
                showOnFocus={false}
                tabIndex={13}
                container={dialogContainer}
              />
            )}
          />
        </div>

        <div className="label-input-group">
          <label htmlFor="heparin">{t('sire.heparinConcentration')}</label>
          <Controller
            name="heparin"
            control={control}
            render={({ field }) => (
              <InputNumber
                id={field.name}
                value={field.value}
                className="w-full"
                onChange={(e) => field.onChange(e.value)}
                tabIndex={14}
              />
            )}
          />
        </div>

        <div className="label-input-group">
          <label htmlFor="breedReg">{t('sire.breedRegistrationCane')}</label>
          <Controller
            name="breedReg"
            control={control}
            render={({ field }) => (
              <InputText
                id={field.name}
                value={field.value}
                className="w-full"
                onChange={(e) => field.onChange(e.target.value)}
                tabIndex={15}
              />
            )}
          />
        </div>

        {isSireVerifiedEnabled && (
          <div className="label-input-group">
            <label htmlFor="verified">{t('sire.verified')}</label>
            <Controller
              name="verified"
              control={control}
              render={({ field }) => (
                <Dropdown
                  inputId={field.name}
                  data-testid="verified"
                  value={field.value}
                  options={verifiedOptions}
                  className="w-full"
                  onChange={(e) => field.onChange(e.value)}
                  tabIndex={16}
                />
              )}
            />
          </div>
        )}
      </form>
    </Dialog>
  );
};
